import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, X, Search, Pencil } from 'lucide-react';
import { api } from '../../lib/api';

interface PlaceSuggestion {
  description: string;
  placeId: string;
}

export interface SelectedPlace {
  address: string;
  placeId: string;
}

interface SuggestionsResponse {
  success: boolean;
  data: PlaceSuggestion[];
}

interface AddressSearchProps {
  value: SelectedPlace | null;
  onChange: (value: SelectedPlace | null) => void;
  label?: string;
  required?: boolean;
  id?: string;
}

export function AddressSearch({ value, onChange, label = 'Address', required, id = 'address-search' }: AddressSearchProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [editing, setEditing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown and exit edit mode when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setEditing(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Focus the input when entering edit mode
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!input.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setFetching(true);
    try {
      const res = await api.get<SuggestionsResponse>(`/places/autocomplete?input=${encodeURIComponent(input)}`);
      setSuggestions(res.data);
      setShowDropdown(res.data.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setFetching(false);
    }
  }, []);

  function handleQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  }

  function handleSelect(suggestion: PlaceSuggestion) {
    onChange({ address: suggestion.description, placeId: suggestion.placeId });
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    setEditing(false);
  }

  function handleClear() {
    onChange(null);
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    setEditing(false);
  }

  function handleEditClick() {
    setEditing(true);
    setQuery('');
    setSuggestions([]);
  }

  const inputBase = `w-full h-11 px-3 rounded-lg border bg-white text-small text-midnight
    placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
    transition-colors duration-150`;

  const showInput = !value || editing;

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={id}
          className="block text-[11px] font-bold uppercase tracking-widest text-slate"
        >
          {label}{required && <span className="text-rose ml-1">*</span>}
        </label>
      )}

      {/* Selected address chip — tap to edit */}
      {value && !editing && (
        <button
          type="button"
          onClick={handleEditClick}
          className="flex items-start gap-2 rounded-lg border border-plum/30 bg-plum/5 px-3 py-2.5
                     text-left w-full min-h-[44px] cursor-pointer hover:border-plum/50
                     active:bg-plum/10 transition-colors duration-150"
        >
          <MapPin size={15} className="shrink-0 text-plum mt-0.5" />
          <span className="flex-1 text-small text-midnight leading-snug">{value.address}</span>
          <Pencil size={14} className="shrink-0 text-slate mt-0.5" />
        </button>
      )}

      {/* Search input — shown when no value or editing */}
      {showInput && (
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            {fetching
              ? <div className="w-3.5 h-3.5 border-2 border-slate/30 border-t-slate rounded-full animate-spin" />
              : <Search size={14} className="text-slate" />
            }
          </div>
          <input
            ref={inputRef}
            id={id}
            type="text"
            autoComplete="off"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
            placeholder={editing ? 'Search for a new address…' : 'Start typing an address…'}
            className={`${inputBase} pl-9 border-stone`}
          />
          {/* Cancel / clear button inside input when editing */}
          {editing && (
            <button
              type="button"
              onClick={() => { setEditing(false); setQuery(''); setSuggestions([]); setShowDropdown(false); }}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate
                         hover:text-midnight transition-colors"
              aria-label="Cancel editing"
            >
              <X size={16} />
            </button>
          )}

          {/* Clear address link when editing an existing value */}
          {editing && value && (
            <button
              type="button"
              onClick={handleClear}
              className="mt-1 text-[12px] text-rose hover:text-rose/80 transition-colors"
            >
              Remove address
            </button>
          )}

          {/* Suggestions dropdown */}
          {showDropdown && (
            <ul
              role="listbox"
              className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-stone
                         rounded-xl shadow-brand-md overflow-hidden"
            >
              {suggestions.map(s => (
                <li key={s.placeId} role="option" aria-selected="false">
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                    className="w-full flex items-start gap-2.5 px-3 py-3 text-left
                               hover:bg-mist active:bg-stone transition-colors duration-100
                               border-b border-stone last:border-b-0"
                  >
                    <MapPin size={14} className="shrink-0 text-slate mt-0.5" />
                    <span className="text-small text-midnight leading-snug">{s.description}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
